import { clsx } from 'clsx';
import { useRefresh } from 'react-admin';
import React, { Dispatch, SetStateAction, useState } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import type { RcFile, UploadProps } from 'antd/es/upload';
import { Button, message, Modal, Typography, Upload } from 'antd';

import { ExportMktResponse, productHttp } from '@network';
import cls from './ImportCsv.module.css';
import { FileUnknownOutlined } from '@ant-design/icons';


interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const MAX_FILE_SIZE = 5;
const { Text } = Typography;

const UploadIcon = () => (
  <svg width="82" height="104" viewBox="0 0 82 104" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="79.4286" height="101.032" rx="7" fill="#042E6B" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="15 15"/>
    <path d="M34.0245 54.2029V50.8917H47.182V54.2029H34.0245ZM38.9332 45.6345H42.3024V59.431H38.9332V45.6345Z" fill="white"/>
  </svg>
);

export const ImportCsv = ({ open, setOpen }: Props) => {
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<UploadFile | null>(null);

  const handleCancel = () => {
    onRemove();
    setOpen(false);
  };

  const handleOk = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file.originFileObj as File);

    try {
      setLoading(true);
      const res: ExportMktResponse = await productHttp.importCsvFile(formData);

      if (!res) throw new Error('Upload failed');

      message.success('File uploaded');
      handleCancel();
      onRemove();
      refresh();
    } catch (e: any) {
      const errorMessage =
        e.response?.data?.message ||
        e.response?.data?.error ||
        e.message || 'Upload error';

      console.error(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isCsv = file.type === 'text/csv'
      || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      || file.type === 'application/vnd.ms-excel';

    if (!isCsv) {
      message.error('Only CSV or XLSX files');
      return Upload.LIST_IGNORE;
    }

    const isValidSize = file.size / 1024 / 1024 < MAX_FILE_SIZE;

    if (!isValidSize) {
      message.error(`File bigger than ${MAX_FILE_SIZE}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const onChange: UploadProps['onChange'] = ({ fileList }) => {
    fileList?.length && setFile(fileList[0]);
  };

  const onRemove = () => {
    setFile(null);
  };

  return (
    <Modal
      className={cls.modal}
      open={open}
      title={<div className={cls.modalHeader}>
        <span className={cls.modalTitle}>Import CSV document</span>
        <a className={cls.footerLink} href="products_empty.csv" download>
          <FileUnknownOutlined />
        </a>
      </div>}
      onOk={handleOk}
      onCancel={handleCancel}
      centered
      closable={false}
      footer={[
        <Button
          key="back"
          className={clsx(cls.btn, cls.btnCancel)}
          onClick={handleCancel}>Cancel</Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          className={clsx(cls.btn, cls.btnOk)}
          onClick={handleOk}>Ok</Button>,
      ]}
    >
      <div className={cls.importForm}>
        <div className={cls.importFormUpload}>
          <div className={cls.fileBlock}>
            {file ? (
              <div className={cls.fileName}>
                <Text style={{ width: 230 }} ellipsis>{file.name}</Text>
              </div>
            ) : (
              <Upload
                customRequest={async ({ onSuccess }) => setTimeout(() => {
                  onSuccess && onSuccess('ok');
                }, 0)}
                listType="text"
                maxCount={1}
                beforeUpload={beforeUpload}
                onChange={onChange}
                onRemove={onRemove}
                className={cls.uploader}
              >
                <div className={cls.uploadBtn}><UploadIcon /></div>
              </Upload>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
